/**
 * RouteCard copy set. The package is domain- and locale-agnostic: its
 * components accept their user-facing copy through an optional `copy`
 * prop of this shape and default to the English {@link defaultRouteCardCopy}
 * for standalone use. The metro-frontend host builds a localised set from
 * the `route-card` catalog (`buildRouteCardCopy(t)`) and threads it
 * down, so the en render is byte-identical and snapshots are unchanged.
 *
 * Keys mirror the iOS / Android `Localizable` contract for RouteCard.
 */
export interface RouteCardCopySet {
  /** Empty-state title ("No routes found"). */
  readonly emptyTitle: string
  /**
   * Empty-state subtitle ("Try a different origin or destination") — the
   * routing produced no variant. iOS / Android show the same
   * `route_card_empty_subtitle`.
   */
  readonly emptySubtitle: string
  /** Error-state title ("Couldn't build route"). */
  readonly errorTitle: string
  /** Error-state default subtitle (network failure). */
  readonly errorNetworkMessage: string
  /** Retry button label. */
  readonly retry: string
  /** "Route Details" CTA label. */
  readonly routeDetails: string
  /** Close button accessible label. */
  readonly closeAria: string
  /** Swap From/To button accessible label. */
  readonly swapAria: string
  /** My-location button accessible label. */
  readonly myLocationAria: string
  /** Results list accessible label — `{{count}} route options`. */
  readonly resultsListAria: (count: number) => string
  /** Tablet header ("Build route"). */
  readonly buildRoute: string
  /** From-pill placeholder ("From"). */
  readonly from: string
  /** To-pill placeholder ("To"). */
  readonly to: string
}

export const defaultRouteCardCopy: RouteCardCopySet = {
  emptyTitle: 'No routes found',
  emptySubtitle: 'Try a different origin or destination',
  errorTitle: "Couldn't build route",
  errorNetworkMessage: 'Network timeout. Check your connection.',
  retry: 'Retry',
  routeDetails: 'Route Details',
  closeAria: 'Close route',
  swapAria: 'Swap From and To',
  myLocationAria: 'My location',
  resultsListAria: (count: number) => `${count} route options`,
  buildRoute: 'Build route',
  from: 'From',
  to: 'To',
}
